import {Inject, Injectable} from '@angular/core';
import {ApiClientConfig} from '../api-client-config';
import {HttpClient, HttpRequest} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ColumnWithLocalization} from "it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization";

import {
  RequestLogDetailResponse,
  ResourceCountResponse,
  WorkspaceItem,
  WorkspaceItemList, WorkspaceItemType,
  WorkspaceMoveRequest,
  WorkspaceParentItem,
  WorkspaceParentTreeResponse
} from '@primatoo-ms-organization';
import {ApiClientUtils} from '../../api-client.utils';
import {DashboardItem} from "it/primatoo/openapi/api/org/schemas/DashboardItem";

@Injectable({
  providedIn: 'root'
})
export class WorkspaceService {
  private readonly endPoint!: string;
  private readonly workspaceEndPoint!: string;
  private readonly dashboardEndPoint!: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    private httpClient: HttpClient) {
    this.endPoint = this.config.endPoint + '/api/org/';
    this.workspaceEndPoint = this.endPoint + 'workspace/';
    this.dashboardEndPoint = this.endPoint + 'dashboard/';
  }

  /**
   * API dedicata al workspace
   */
  public workspaceSearch(name: string): Observable<WorkspaceItem[]> {
    const url = this.workspaceEndPoint + 'search-by-name?name=' + name;
    return this.httpClient.get<WorkspaceItemList>(url).pipe(map(r => r.items));
  }

  public uploadAttachment(file: File, folderId: number): Observable<any> {
    const url = this.workspaceEndPoint + `${folderId}/upload-attachment`;
    const formData = new FormData();
    if (file) {
      formData.append('file', file, file.name);
      const uploadRequest = new HttpRequest('POST', url, formData, {reportProgress: true});
      return this.httpClient.request<void>(uploadRequest);
    }
    const uploadRequest = new HttpRequest('POST', url, formData, {reportProgress: true});
    return this.httpClient.request<void>(uploadRequest);
  }

  public move(idItem: number, workspaceType: WorkspaceItemType, resourceMoveRequest: WorkspaceMoveRequest): Observable<WorkspaceItem> {
    const url = this.workspaceEndPoint + `${workspaceType}/${idItem}/move`;
    return this.httpClient.post<WorkspaceItem>(url, resourceMoveRequest);
  }

  public workspaceItemParents(resourceId: number): Observable<WorkspaceParentItem[]> {
    const url = this.workspaceEndPoint + `FOLDER/${resourceId}/parent-tree/`;
    return this.httpClient.get<WorkspaceParentTreeResponse>(url).pipe(map(r => r.tree));
  }

  public getWorkspaceRoot(): Observable<WorkspaceItem> {
    const url = this.workspaceEndPoint + `root`;
    return this.httpClient.get<WorkspaceItem>(url);
  }

  public getWorkspaceAttachment(): Observable<WorkspaceItem> {
    const url = this.workspaceEndPoint + `attachment`;
    return this.httpClient.get<WorkspaceItem>(url);
  }

  public getFolderChildren(parentId: number): Observable<WorkspaceItem[]> {
    const url = this.workspaceEndPoint + `${parentId}/children/`;
    return this.httpClient.get<WorkspaceItemList>(url).pipe(
      map(items => items.items)
    );
  }

  public getWorkspaceColEnabled(): Observable<ColumnWithLocalization[]> {
    const url = this.workspaceEndPoint + `columns/`;
    return this.httpClient.get<ColumnWithLocalization[]>(url);
  }


  /*
   * Dashboard
   */
  public getResourcesDashboardLogsDetails(resourceType: DashboardItem, from: number, to: number): Observable<RequestLogDetailResponse> {
    const url = this.dashboardEndPoint + `logs/detail/`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const params = ApiClientUtils.buildAndAppendHttpParams({type: resourceType, from, to, timezone});
    return this.httpClient.get<RequestLogDetailResponse>(url, {params}).pipe();
  }

  public getResourcesDashboardCount(resources?: DashboardItem[]): Observable<Array<DashboardResourcesCountResponse>> {
    const url = this.dashboardEndPoint + `count/`;
    const params = ApiClientUtils.buildAndAppendHttpParams({types: ApiClientUtils.buildArrayFilter(resources)});
    return this.httpClient.get<any>(url, {params}).pipe(
      map((response: Array<Map<DashboardItem, number>>) =>
        DashboardResourcesCount(response, resources)
      )
    );
  }


  public getResourcesDashboardLogs(resourceType: DashboardItem, from: number, to: number): Observable<ResourceCountResponse> {
    const url = this.dashboardEndPoint + `logs/daily/`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const params = ApiClientUtils.buildAndAppendHttpParams({type: resourceType, from, to, timezone});
    return this.httpClient.get<ResourceCountResponse>(url, {params});
  }

}

export interface DashboardResourcesCountResponse {
  name: DashboardItem,
  count: number
}

function DashboardResourcesCount(response: Map<DashboardItem, number>[], resources?: DashboardItem[]): Array<DashboardResourcesCountResponse> {
  const result: DashboardResourcesCountResponse[] = [];
  for (const key in response) {
    if (Object.values(DashboardItem).includes(key as DashboardItem) && typeof response[key] === 'number') {
      result.push({name: key as DashboardItem, count: Number(response[key])});
    }
  }
  if (resources) {
    const resourcesOrder = resources.reduce((order, resource, index) => ({
      ...order,
      [resource]: index
    }), {});
    return result.sort((a, b) =>
      resourcesOrder[a.name] - resourcesOrder[b.name]);
  }
  return result;
}
